/**
 * PSP/CRE Tool v2.0 - Staging Facility Standards
 * 
 * Purpose: Define NATO standards for advanced medical staging facilities
 * with Ukraine 2022-2024 validated requirements and icons.js integration.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 30, 2024
 * Version: 2.2 - SF-004: Added Parent MTF Linkage Schema
 * 
 * Facility Types:
 * - CSU (Casualty Staging Unit): Pre-hospital patient staging and triage
 * - PHA (Patient Holding Area): Post-surgical patient holding before evacuation
 * - HLZ (Helicopter Landing Zone): Air evacuation infrastructure
 * - FST (Forward Surgical Team): Attachable surgical capability for Role 2B
 * - FRS (Forward Resuscitation System): Enhanced surgical + ICU capability
 * 
 * SF-004 Addition (Dec 30, 2024):
 * - STAGING_FACILITY_SCHEMA: Template for creating staging facilities
 * - parent_mtf_id: Links staging facility to specific MTF it supports
 * - distance_to_parent_km: Distance from staging facility to parent MTF
 * - PARENT_MTF_ASSIGNMENT_OPTIONS: UI dropdown options for parent MTF selection
 * - createStagingFacility(): Helper to create properly linked staging facilities
 * - validateStagingFacilityParentLink(): Validates parent MTF linkage
 * 
 * CC-005 Addition (Dec 30, 2024):
 * - FST_NATO_STANDARDS: Forward Surgical Team specifications
 * - FRS_NATO_STANDARDS: Forward Resuscitation System specifications
 * - FST/FRS attachment to Role 2 Basic for Golden Hour compliance
 * - Integration with casualtyCalculator hasSurgicalCapability()
 * 
 * References:
 * - NATO STANAG 2542 (AJMedP-1) Allied Joint Medical Planning Doctrine
 * - NATO STANAG 2561 (AJMedP-5) Aeromedical Evacuation
 * - US Army ATP 4-02.2 Medical Evacuation
 * - US Army ATP 4-02.25 Forward Surgical Team Operations
 * - Romanian Army Medical Operations Doctrine (2025)
 * - Ukraine 2022-2024 medical staging data
 * 
 * Usage:
 * import { 
 *   CSU_NATO_STANDARDS,
 *   PHA_NATO_STANDARDS,
 *   HLZ_NATO_STANDARDS,
 *   FST_NATO_STANDARDS,
 *   FRS_NATO_STANDARDS,
 *   STAGING_FACILITY_SCHEMA,
 *   createStagingFacility,
 *   getFacilityStandards,
 *   calculateFacilityRequirements
 * } from '../constants/stagingFacilityStandards';
 */

import { FACILITY_ICONS, STATUS_ICONS, TRANSPORT_ICONS, MEDICAL_ICONS } from '../utils/icons';

// ============================================================================
// CASUALTY STAGING UNIT (CSU) STANDARDS
// ============================================================================

/**
 * CSU - Casualty Staging Unit
 * 
 * Purpose: Pre-hospital staging area for casualties awaiting treatment or evacuation
 * Location: Forward of Role 2 facilities, near point of injury
 * Function: Initial triage, stabilization, preparation for evacuation
 */
export const CSU_NATO_STANDARDS = {
  SMALL: {
    size: 'SMALL',
    label: 'Small CSU',
    icon: FACILITY_ICONS.CSU,
    
    capacity_patients: 30,
    beds_stretcher: 20,
    beds_ambulatory: 10,
    
    personnel: {
      medical_officers: 1,        // Triage officer, emergency medicine trained
      nurses: 3,                  // Trauma nurses for stabilization
      medics: 6,                  // Combat medics for basic care
      support_staff: 3,           // Admin, logistics, patient tracking
      total: 13
    },
    
    daily_supply_consumption: {
      iv_fluids_liters: 50,       // Crystalloids for resuscitation
      dressings_units: 100,       // Bandages, gauze, trauma dressings
      medications_doses: 120,     // Pain control, antibiotics, TXA
      food_rations: 90,           // For patients and staff
      blood_units: 6,             // Emergency O-negative blood
      oxygen_cylinders: 4         // For respiratory support
    },
    
    equipment: {
      stretchers: 20,
      medical_supplies: 'Basic trauma and stabilization',
      communications: 'Radio, secure data link',
      power: 'Generator, 10 kW',
      environmental: 'Tents with heating/cooling'
    },
    
    setup_time_hours: 2,
    breakdown_time_hours: 1.5,
    
    recommended_for: "Brigade-level operations",
    typical_location: "5-10 km from FEBA, near Role 1 facilities",
    echelon: 'BRIGADE',
    
    ukraine_data: {
      typical_capacity: 25,
      underground_preferred: true,
      displacement_frequency: 'Every 2-3 days in active sector',
      lessons: [
        'Underground CSUs dramatically reduced losses',
        'Mobile CSU in vehicles improved flexibility',
        'Integration with civilian volunteers helpful'
      ]
    },
    
    notes: "Suitable for sustained operations with 200-300 daily casualties. " +
           "Can operate independently for 48-72 hours with organic supplies."
  },
  
  MEDIUM: {
    size: 'MEDIUM',
    label: 'Medium CSU',
    icon: FACILITY_ICONS.CSU,
    
    capacity_patients: 50,
    beds_stretcher: 30,
    beds_ambulatory: 20,
    
    personnel: {
      medical_officers: 1,        // Senior triage officer
      nurses: 4,                  // Enhanced nursing support
      medics: 8,                  // Larger medic team
      support_staff: 4,           // Enhanced admin and logistics
      total: 17
    },
    
    daily_supply_consumption: {
      iv_fluids_liters: 80,
      dressings_units: 150,
      medications_doses: 200,
      food_rations: 150,
      blood_units: 10,
      oxygen_cylinders: 8
    },
    
    equipment: {
      stretchers: 30,
      medical_supplies: 'Enhanced trauma and stabilization kit',
      communications: 'Dual radio systems, satellite link',
      power: 'Generator, 20 kW with backup',
      environmental: 'Hardened shelters with HVAC'
    },
    
    setup_time_hours: 4,
    breakdown_time_hours: 3,
    
    recommended_for: "Division-level operations",
    typical_location: "10-15 km from FEBA, co-located with Role 2",
    echelon: 'DIVISION',
    
    ukraine_data: {
      typical_capacity: 45,
      hardened_structure_required: true,
      displacement_frequency: 'Weekly in active sector',
      lessons: [
        'School basements ideal for medium CSU',
        'Multiple exits essential for survivability',
        'Generator protection critical'
      ]
    },
    
    notes: "Designed for high-intensity operations with 400-600 daily casualties. " +
           "Can support multiple Role 1 facilities. Self-sustaining for 72-96 hours."
  },
  
  LARGE: {
    size: 'LARGE',
    label: 'Large CSU',
    icon: FACILITY_ICONS.CSU,
    
    capacity_patients: 100,
    beds_stretcher: 60,
    beds_ambulatory: 40,
    
    personnel: {
      medical_officers: 2,        // Dual triage teams for 24/7 operations
      nurses: 8,                  // Multiple trauma nurse teams
      medics: 16,                 // Large medic contingent
      support_staff: 8,           // Full admin, logistics, and patient tracking
      total: 34
    },
    
    daily_supply_consumption: {
      iv_fluids_liters: 160,
      dressings_units: 300,
      medications_doses: 400,
      food_rations: 300,
      blood_units: 20,
      oxygen_cylinders: 16
    },
    
    equipment: {
      stretchers: 60,
      medical_supplies: 'Full trauma center, with ventilators and monitors',
      communications: 'Redundant systems, tactical internet connectivity',
      power: 'Dual generators, 40 kW total with UPS',
      environmental: 'Modular facilities with full HVAC and NBC protection'
    },
    
    setup_time_hours: 8,
    breakdown_time_hours: 6,
    
    recommended_for: "Corps-level operations",
    typical_location: "15-25 km from FEBA, near Role 3 facility",
    echelon: 'CORPS',
    
    ukraine_data: {
      typical_capacity: 80,
      permanent_structure_required: true,
      air_defense_coverage_recommended: true,
      lessons: [
        'Large CSUs targeted by missiles - dispersion critical',
        'Integration with civilian hospitals effective',
        'Train station proximity enabled mass evacuation'
      ]
    },
    
    notes: "Supports corps-level operations with 800-1200 daily casualties. " +
           "Can coordinate multiple division CSUs. Self-sustaining for 5-7 days. " +
           "Often includes blood bank and basic surgical capability."
  }
};

// ============================================================================
// PATIENT HOLDING AREA (PHA) STANDARDS
// ============================================================================

/**
 * PHA - Patient Holding Area
 * 
 * Purpose: Post-surgical holding area for stabilized patients awaiting evacuation
 * Location: Adjacent to Role 2/3 surgical facilities
 * Function: Post-operative care, stabilization before air/ground evacuation
 */
export const PHA_NATO_STANDARDS = {
  SMALL: {
    size: 'SMALL',
    label: 'Small PHA',
    icon: FACILITY_ICONS.PHA,
    
    capacity_patients: 20,
    beds_total: 20,
    hold_time_hours: 48,
    
    personnel: {
      medical_officers: 1,
      nurses: 2,
      medics: 4,
      support_staff: 2,
      total: 9
    },
    
    daily_supply_consumption: {
      iv_fluids_liters: 30,
      dressings_units: 60,
      medications_doses: 80,
      food_rations: 60,
      blood_units: 4,
      oxygen_cylinders: 4
    },
    
    equipment: {
      beds: 20,
      medical_supplies: 'Post-operative monitoring, oxygen, suction',
      communications: 'Radio link to surgical team and evacuation control',
      power: 'Generator, 15 kW',
      environmental: 'Climate-controlled tents'
    },
    
    setup_time_hours: 3,
    breakdown_time_hours: 2,
    
    recommended_for: "Brigade-level operations",
    typical_location: "Adjacent to Role 2 Basic facility",
    echelon: 'BRIGADE',
    
    ukraine_data: {
      lessons: [
        'Post-op patients most vulnerable during evacuation',
        'Warming capability essential',
        'Blood product availability critical'
      ]
    },
    
    notes: "Designed for post-surgical patients from Role 2 FST. " +
           "Holds patients until stable for ground evacuation to Role 3."
  },
  
  MEDIUM: {
    size: 'MEDIUM',
    label: 'Medium PHA',
    icon: FACILITY_ICONS.PHA,
    
    capacity_patients: 40,
    beds_total: 40,
    hold_time_hours: 48,
    
    personnel: {
      medical_officers: 1,
      nurses: 3,
      medics: 6,
      support_staff: 3,
      total: 13
    },
    
    daily_supply_consumption: {
      iv_fluids_liters: 60,
      dressings_units: 120,
      medications_doses: 160,
      food_rations: 120,
      blood_units: 8,
      oxygen_cylinders: 8
    },
    
    equipment: {
      beds: 40,
      medical_supplies: 'Advanced monitoring, ventilators (4), ICU-level support',
      communications: 'Integrated with Role 2/3 systems',
      power: 'Generator, 25 kW with backup',
      environmental: 'Hardened shelters with full climate control'
    },
    
    setup_time_hours: 5,
    breakdown_time_hours: 4,
    
    recommended_for: "Division-level operations",
    typical_location: "Adjacent to Role 2 Enhanced or Role 3 facility",
    echelon: 'DIVISION',
    
    ukraine_data: {
      lessons: [
        'Weather delays require extended holding capacity',
        'ICU capability essential for critical patients',
        'Helicopter availability drives hold times'
      ]
    },
    
    notes: "Supports division-level surgical volume. " +
           "Can hold patients during weather delays or MEDEVAC shortages."
  },
  
  LARGE: {
    size: 'LARGE',
    label: 'Large PHA',
    icon: FACILITY_ICONS.PHA,
    
    capacity_patients: 80,
    beds_total: 80,
    hold_time_hours: 72,
    
    personnel: {
      medical_officers: 2,
      nurses: 6,
      medics: 12,
      support_staff: 6,
      total: 26
    },
    
    daily_supply_consumption: {
      iv_fluids_liters: 120,
      dressings_units: 240,
      medications_doses: 320,
      food_rations: 240,
      blood_units: 16,
      oxygen_cylinders: 16
    },
    
    equipment: {
      beds: 80,
      medical_supplies: 'Full ICU capability, ventilators (12)',
      communications: 'Full integration with Role 3 and STRATEVAC',
      power: 'Dual generators, 50 kW with UPS',
      environmental: 'Permanent or semi-permanent structures'
    },
    
    setup_time_hours: 10,
    breakdown_time_hours: 8,
    
    recommended_for: "Corps-level operations",
    typical_location: "Integral to Role 3 CSH complex",
    echelon: 'CORPS',
    
    ukraine_data: {
      lessons: [
        'Train evacuation capability essential for overflow',
        'Strategic evacuation delays require extended hold',
        'Integration with civilian hospitals for surge'
      ]
    },
    
    notes: "Integral to Role 3 operations. Manages patient flow to strategic evacuation. " +
           "Can handle 72+ hour holds during weather or capacity constraints."
  }
};

// ============================================================================
// HELICOPTER LANDING ZONE (HLZ) STANDARDS
// ============================================================================

/**
 * HLZ - Helicopter Landing Zone
 * 
 * Purpose: Air evacuation infrastructure for MEDEVAC operations
 * Location: Near medical facilities at all echelons
 * Function: Helicopter landing, patient loading/unloading, refueling
 */
export const HLZ_NATO_STANDARDS = {
  SMALL: {
    size: 'SMALL',
    label: 'Small HLZ (Point)',
    icon: FACILITY_ICONS.HLZ,
    
    capacity_helicopters: 1,
    aircraft_types: ['UH-60', 'IAR-330', 'Mi-17'],
    
    personnel: {
      heli_control: 1,
      medics: 2,
      security: 2,
      total: 5
    },
    
    equipment: {
      landing_pads: 1,
      lighting: 'Basic night lighting, chemical lights',
      communications: 'FM radio, visual signals',
      refueling: 'None (forward HLZ)',
      medical_staging: 'Stretchers and litters only'
    },
    
    setup_time_hours: 0.5,
    breakdown_time_hours: 0.25,
    
    dimensions: {
      diameter_m: 35,
      approach_clear_m: 100,
      surface: 'Cleared grass or hardstand'
    },
    
    recommended_for: "Battalion-level operations",
    typical_location: "Adjacent to Role 1 BAS, 3-5 km from FEBA",
    echelon: 'BATTALION',
    
    throughput: {
      missions_per_hour: 2,
      patients_per_hour: 8
    },
    
    ukraine_data: {
      lessons: [
        'Small HLZ most survivable - rapid in/out',
        'Concealment critical - drone observation constant',
        'Night operations preferred in contested areas',
        'Multiple HLZ locations for unpredictability'
      ]
    },
    
    notes: "Rapid setup point HLZ for emergency MEDEVAC. Single helicopter at a time. " +
           "Typically battalion-level for immediate T1 evacuation."
  },
  
  MEDIUM: {
    size: 'MEDIUM',
    label: 'Medium HLZ',
    icon: FACILITY_ICONS.HLZ,
    
    capacity_helicopters: 4,
    aircraft_types: ['UH-60', 'IAR-330', 'Mi-17', 'CH-47'],
    
    personnel: {
      heli_control: 2,
      medics: 4,
      security: 4,
      refuel: 2,
      total: 12
    },
    
    equipment: {
      landing_pads: 4,
      lighting: 'Full night lighting, NVG compatible, IR markers',
      communications: 'Dual radios, satellite link',
      refueling: 'Limited FARP capability (2 aircraft)',
      weather: 'Basic weather station',
      medical_staging: 'Small CSU capability adjacent'
    },
    
    setup_time_hours: 4,
    breakdown_time_hours: 3,
    
    dimensions: {
      diameter_m: 100,
      approach_clear_m: 200,
      surface: 'Improved surface or hardstand'
    },
    
    recommended_for: "Brigade/Division-level operations",
    typical_location: "Near Role 2, 15-25 km from FEBA",
    echelon: 'DIVISION',
    
    throughput: {
      missions_per_hour: 8,
      patients_per_hour: 32
    },
    
    ukraine_data: {
      lessons: [
        'Medium HLZ balance between capacity and survivability',
        'Hardened refuel points improved availability',
        'Counter-drone systems increasingly important',
        'Dispersion of landing pads reduced losses'
      ]
    },
    
    notes: "Brigade or division HLZ with multiple pads for sustained operations. " +
           "Can handle CH-47 for mass casualty evacuation. Supports 10-15 MEDEVAC missions per hour."
  },
  
  LARGE: {
    size: 'LARGE',
    label: 'Large HLZ (FARP)',
    icon: FACILITY_ICONS.HLZ,
    
    capacity_helicopters: 8,
    aircraft_types: ['UH-60', 'IAR-330', 'Mi-17', 'CH-47', 'Mi-8AMT'],
    
    personnel: {
      heli_control: 4,
      medics: 8,
      security: 8,
      refuel: 6,
      maintenance: 4,
      total: 30
    },
    
    equipment: {
      landing_pads: 8,
      lighting: 'Full airfield-standard lighting, NVG and IR',
      communications: 'Full tower capability, multiple radios, satellite, datalink',
      refueling: 'Full FARP, 4 refueling points, bulk fuel storage',
      maintenance: 'Field maintenance capability, spare parts inventory',
      weather: 'Tactical weather station, forecasting capability',
      medical_staging: 'Integrated CSU/PHA adjacent to HLZ'
    },
    
    setup_time_hours: 12,
    breakdown_time_hours: 8,
    
    dimensions: {
      diameter_m: 250,
      approach_clear_m: 500,
      surface: 'Prepared surface, PSP or concrete'
    },
    
    recommended_for: "Corps-level operations",
    typical_location: "Corps main HLZ at Role 3 CSH",
    echelon: 'CORPS',
    
    throughput: {
      missions_per_hour: 20,
      patients_per_hour: 80
    },
    
    ukraine_data: {
      lessons: [
        'Large HLZ high-value targets - air defense essential',
        'Integration with airfield operations improved efficiency',
        'Night-only operations in contested airspace',
        'Dispersion into smaller sites during high threat'
      ]
    },
    
    notes: "Major air hub for corps area. Functions as mini-airfield for MEDEVAC. " +
           "Can handle all NATO rotary-wing aircraft. " +
           "FARP supports attack/utility helicopters in addition to MEDEVAC."
  }
};

// ============================================================================
// CC-005: FORWARD SURGICAL TEAM (FST) STANDARDS
// ============================================================================

/**
 * FST - Forward Surgical Team
 * 
 * Purpose: Bring surgical capability forward to meet Golden Hour requirements
 * Location: Attached to Role 2 Basic when Golden Hour cannot be met
 * Function: Damage Control Surgery (DCS), resuscitation, stabilization
 * 
 * Key Capability: When attached to Role 2 Basic, enables surgery at brigade level
 * This converts Role 2B from "stabilization only" to "damage control surgery capable"
 * 
 * Ukraine 2022-2024: FST attachment to forward Role 2s significantly improved survival
 * when evacuation times exceeded Golden Hour threshold.
 */
export const FST_NATO_STANDARDS = {
  STANDARD: {
    code: 'FST',
    name: 'Forward Surgical Team',
    nato_designation: 'FST',
    icon: MEDICAL_ICONS.SURGERY || '🔪',
    
    // Team composition
    personnel: {
      surgeons: 3,           // General/trauma surgeons
      anesthesiologists: 1,   // Anesthesia provider
      crna: 1,               // Certified RN Anesthetist
      or_nurses: 4,          // Operating room nurses
      surgical_techs: 2,     // Surgical technicians
      medics: 4,             // Combat medics for pre/post-op
      admin: 1,              // Admin/logistics
      total: 16
    },
    
    // Surgical capability
    capability: {
      simultaneous_tables: 2,
      procedures_per_day: 10,
      max_procedures_per_day: 16,  // Surge capacity
      specialties: ['General Surgery', 'Orthopedic', 'Trauma'],
      dcs_capable: true,
      definitive_surgery_capable: false,
      neurosurgery: false,
      vascular: 'BASIC',           // Shunts, not definitive repair
      thoracic: 'DAMAGE_CONTROL'   // Tube thoracostomy, thoracotomy for hemorrhage control
    },
    
    // Equipment
    equipment: {
      operating_tables: 2,
      ventilators: 4,
      monitors: 6,
      suction_units: 4,
      electrosurgery: 2,
      sterilizer: 1,
      xray: false,
      ultrasound: true,
      blood_warmer: 2
    },
    
    // Logistics
    consumables_days: 3,
    blood_storage_units: 20,
    
    // Deployability
    deployability: {
      setup_time_hours: 4,
      teardown_time_hours: 2,
      transport_vehicles: 2,
      weight_kg: 3000,
      can_be_helicopter_inserted: true,
      max_altitude_ft: 8000
    },
    
    // Attachment configuration
    attachment: {
      typical_attachment_to: 'ROLE2_BASIC',
      enables_surgery_at: 'BRIGADE',
      golden_hour_impact: 'Converts R2B to surgical destination',
      phase_availability: 'Can be attached for specific operational phases',
      detach_time_hours: 2
    },
    
    // Impact when attached to Role 2 Basic
    when_attached_to_role2b: {
      has_surgical_capability: true,
      surgical_procedures_per_day: 10,
      dcs_capable: true,
      reduces_time_to_surgery_by_min: 45,  // Avg savings vs evacuating to R2E
      golden_hour_compliance_improvement: 0.35  // 35% more casualties meet GH
    },
    
    // Ukraine 2022-2024 experience
    ukraine_data: {
      deployment_rate: 0.40,  // 40% of brigades had FST attached at some point
      survival_improvement: 0.22,  // 22% absolute improvement in T1 survival
      average_setup_time_hours: 3.5,
      challenges: [
        'Power generation in austere environments',
        'Blood product logistics',
        'Surgeon fatigue in sustained operations'
      ],
      lessons: [
        'FST attachment decision should be based on expected evacuation times',
        'Pre-position FST for planned high-intensity phases',
        'Rotate surgeons every 72h max in sustained ops'
      ]
    },
    
    notes: "FST provides damage control surgery forward. All post-op patients MUST " +
           "be evacuated to Role 2E/Role 3 within 24-48h. FST cannot hold patients long-term. " +
           "Attach when expected POI→Surgery time exceeds 60 minutes."
  },
  
  LIGHT: {
    code: 'FST-L',
    name: 'Forward Surgical Team (Light)',
    nato_designation: 'FST-L',
    icon: MEDICAL_ICONS.SURGERY || '🔪',
    
    personnel: {
      surgeons: 2,
      anesthesiologists: 1,
      or_nurses: 2,
      surgical_techs: 1,
      medics: 2,
      total: 8
    },
    
    capability: {
      simultaneous_tables: 1,
      procedures_per_day: 6,
      max_procedures_per_day: 10,
      specialties: ['General Surgery', 'Trauma'],
      dcs_capable: true,
      definitive_surgery_capable: false
    },
    
    equipment: {
      operating_tables: 1,
      ventilators: 2,
      monitors: 3,
      suction_units: 2,
      electrosurgery: 1,
      sterilizer: 0,  // Uses sterile packs
      xray: false,
      ultrasound: true
    },
    
    consumables_days: 2,
    blood_storage_units: 10,
    
    deployability: {
      setup_time_hours: 2,
      teardown_time_hours: 1,
      transport_vehicles: 1,
      weight_kg: 1500,
      can_be_helicopter_inserted: true
    },
    
    when_attached_to_role2b: {
      has_surgical_capability: true,
      surgical_procedures_per_day: 6,
      dcs_capable: true,
      reduces_time_to_surgery_by_min: 45,
      golden_hour_compliance_improvement: 0.25
    },
    
    notes: "Lighter, faster-deploying FST variant. Single table, limited endurance. " +
           "Ideal for airborne/air assault operations or rapid reinforcement."
  }
};

// ============================================================================
// CC-005: FORWARD RESUSCITATION SYSTEM (FRS) STANDARDS
// ============================================================================

/**
 * FRS - Forward Resuscitation System (US Army specific)
 * 
 * Similar to FST but with enhanced resuscitation focus.
 * Includes additional ICU capability for post-op stabilization.
 */
export const FRS_NATO_STANDARDS = {
  STANDARD: {
    code: 'FRS',
    name: 'Forward Resuscitation System',
    nato_designation: 'FRS',
    icon: MEDICAL_ICONS.SURGERY || '🔪',
    
    personnel: {
      surgeons: 2,
      anesthesiologists: 1,
      emergency_physicians: 1,
      crna: 1,
      or_nurses: 3,
      icu_nurses: 2,
      surgical_techs: 2,
      medics: 4,
      total: 16
    },
    
    capability: {
      simultaneous_tables: 1,
      procedures_per_day: 8,
      max_procedures_per_day: 12,
      specialties: ['General Surgery', 'Trauma', 'Emergency Medicine'],
      dcs_capable: true,
      definitive_surgery_capable: false,
      icu_beds: 4,
      ventilator_capable: true,
      resuscitation_focus: true
    },
    
    equipment: {
      operating_tables: 1,
      icu_beds: 4,
      ventilators: 6,
      monitors: 8,
      suction_units: 4,
      electrosurgery: 1,
      sterilizer: 1,
      xray: false,
      ultrasound: true,
      blood_warmer: 2,
      rapid_infuser: 2
    },
    
    consumables_days: 3,
    blood_storage_units: 30,
    
    deployability: {
      setup_time_hours: 6,
      teardown_time_hours: 4,
      transport_vehicles: 3,
      weight_kg: 4500,
      can_be_helicopter_inserted: false  // Too heavy
    },
    
    when_attached_to_role2b: {
      has_surgical_capability: true,
      surgical_procedures_per_day: 8,
      dcs_capable: true,
      icu_beds_added: 4,
      reduces_time_to_surgery_by_min: 45,
      golden_hour_compliance_improvement: 0.30
    },
    
    notes: "FRS emphasizes resuscitation and short-term ICU hold. " +
           "Can stabilize patients longer than FST (up to 72h). " +
           "Heavier footprint but more sustained capability."
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Facility size options for dropdowns
 */
export const FACILITY_SIZE_OPTIONS = [
  { value: 'SMALL', label: 'Small', icon: STATUS_ICONS.GREEN, description: 'Brigade-level operations' },
  { value: 'MEDIUM', label: 'Medium', icon: STATUS_ICONS.YELLOW, description: 'Division-level operations' },
  { value: 'LARGE', label: 'Large', icon: STATUS_ICONS.RED, description: 'Corps-level operations' }
];

/**
 * Facility type options for dropdowns
 * CC-005: Added FST and FRS
 */
export const FACILITY_TYPE_OPTIONS = [
  { value: 'PHA', label: 'Patient Holding Area', icon: FACILITY_ICONS.PHA, description: 'Forward holding at Role 2B (AJP-4.10)' },
  { value: 'CSU', label: 'Casualty Staging Unit', icon: FACILITY_ICONS.CSU, description: 'STRATEVAC staging at APOD (AJMedP-1 para 3.18.6)' },
  { value: 'HLZ', label: 'Helicopter Landing Zone', icon: FACILITY_ICONS.HLZ, description: 'Air evacuation' },
  { value: 'FST', label: 'Forward Surgical Team', icon: MEDICAL_ICONS.SURGERY || '🔪', description: 'Forward surgical capability' },
  { value: 'FRS', label: 'Forward Resuscitation System', icon: MEDICAL_ICONS.SURGERY || '🔪', description: 'Forward surgery + ICU' }
];

// ============================================================================
// SF-004: PARENT MTF LINKAGE SCHEMA
// ============================================================================

/**
 * Parent MTF assignment options for UI dropdown
 * Staging facilities can be assigned to support any echelon level
 * SF-004: Added to enable linking CSU/PHA/HLZ to specific MTFs
 */
export const PARENT_MTF_ASSIGNMENT_OPTIONS = [
  { 
    value: 'BATTALION', 
    label: 'Battalion Level (Near Role 1)', 
    icon: STATUS_ICONS.GREEN,
    typical_facilities: ['PHA', 'HLZ'],
    description: 'Forward PHA for MASCAL collection, HLZ for urgent evacuation'
  },
  {
    value: 'BRIGADE',
    label: 'Brigade Level (Near Role 2 Basic)',
    icon: STATUS_ICONS.YELLOW,
    typical_facilities: ['PHA', 'HLZ', 'FST'],
    description: 'Standard location for brigade medical support'
  },
  {
    value: 'DIVISION',
    label: 'Division Level (Near Role 2 Enhanced)',
    icon: STATUS_ICONS.ORANGE,
    typical_facilities: ['PHA', 'HLZ', 'FST', 'FRS'],
    description: 'Division field hospital support'
  },
  {
    value: 'CORPS',
    label: 'Corps Level (Near Role 3)',
    icon: STATUS_ICONS.RED,
    typical_facilities: ['CSU', 'PHA', 'HLZ'],
    description: 'Strategic evacuation staging and overflow holding'
  },
  {
    value: 'AIRFIELD',
    label: 'Airfield (STRATEVAC)',
    icon: TRANSPORT_ICONS.HELICOPTER || '🚁',
    typical_facilities: ['CSU', 'HLZ'],
    description: 'CSU at APOD — fixed-wing strategic evacuation staging (AJMedP-1 para 3.18.6)'
  }
];

/**
 * SF-004: Staging Facility Schema Template
 * 
 * This schema defines the data model for staging facilities with parent MTF linkage.
 * Used by Tab3_Medical.jsx when creating/editing staging facilities.
 */
export const STAGING_FACILITY_SCHEMA = {
  // Unique identifier for this staging facility
  id: null,                           // e.g., 'csu_bde1_fwd', 'hlz_div1_primary'
  
  // Facility type and configuration
  facility_type: null,                // 'CSU', 'PHA', 'HLZ', 'FST', 'FRS'
  facility_size: null,                // 'SMALL', 'MEDIUM', 'LARGE' (or 'STANDARD', 'LIGHT' for FST)
  facility_name: null,                // Human-readable name, e.g., 'Alpha CSU'
  
  // SF-004: Parent MTF Linkage (NEW)
  parent_mtf_id: null,                // ID of MTF this facility supports, e.g., 'role1_bde1_bn3'
  parent_mtf_type: null,              // 'ROLE1', 'ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3'
  distance_to_parent_km: 0.5,         // Distance from staging facility to parent MTF
  
  // Echelon assignment
  allocated_to_echelon: null,         // 'BATTALION', 'BRIGADE', 'DIVISION', 'CORPS'
  allocated_to_unit_id: null,         // ID of unit (brigade, division, etc.)
  allocated_to_unit_name: null,       // Name for display
  
  // Position
  position_feba_km: null,             // Distance from FEBA in kilometers
  grid_coordinates: null,             // Military grid reference (optional)
  
  // Operational status
  status: 'PLANNED',                  // 'PLANNED', 'SETTING_UP', 'OPERATIONAL', 'DEGRADED', 'RELOCATING'
  operational_from_phase: null,       // Phase when facility becomes operational
  operational_to_phase: null,         // Phase when facility closes (null = permanent)
  
  // For FST/FRS only: attachment info
  attached_to_mtf_id: null,           // If FST attached to Role 2B
  attachment_date: null,
  
  // Metadata
  created_at: null,
  updated_at: null,
  notes: null
};

/**
 * SF-004: Create a new staging facility with proper schema
 * @param {Object} config - Facility configuration
 * @returns {Object} Staging facility object with all required fields
 */
export function createStagingFacility(config) {
  const now = new Date().toISOString();
  
  // Generate ID if not provided
  const id = config.id || `${config.facility_type?.toLowerCase() || 'facility'}_${Date.now()}`;
  
  return {
    ...STAGING_FACILITY_SCHEMA,
    id,
    facility_type: config.facility_type || 'CSU',
    facility_size: config.facility_size || 'MEDIUM',
    facility_name: config.facility_name || `${config.facility_type || 'Staging'} Facility`,
    
    // SF-004: Parent MTF linkage
    parent_mtf_id: config.parent_mtf_id || null,
    parent_mtf_type: config.parent_mtf_type || null,
    distance_to_parent_km: config.distance_to_parent_km ?? 0.5,
    
    // Echelon
    allocated_to_echelon: config.allocated_to_echelon || 'BRIGADE',
    allocated_to_unit_id: config.allocated_to_unit_id || null,
    allocated_to_unit_name: config.allocated_to_unit_name || null,
    
    // Position
    position_feba_km: config.position_feba_km ?? 15,
    grid_coordinates: config.grid_coordinates || null,
    
    // Status
    status: config.status || 'PLANNED',
    operational_from_phase: config.operational_from_phase ?? 1,
    operational_to_phase: config.operational_to_phase || null,
    
    // FST/FRS attachment
    attached_to_mtf_id: config.attached_to_mtf_id || null,
    attachment_date: config.attachment_date || null,
    
    // Metadata
    created_at: now,
    updated_at: now,
    notes: config.notes || null
  };
}

/**
 * SF-004: Validate staging facility parent MTF linkage
 * @param {Object} facility - Staging facility object
 * @param {Array} availableMTFs - Array of available MTF objects to link to
 * @returns {Object} Validation result with errors and warnings
 */
export function validateStagingFacilityParentLink(facility, availableMTFs = []) {
  const errors = [];
  const warnings = [];
  
  // Check if parent_mtf_id is set
  if (!facility.parent_mtf_id) {
    warnings.push({
      field: 'parent_mtf_id',
      message: 'No parent MTF linked - facility may not integrate into evacuation chain',
      icon: STATUS_ICONS.YELLOW
    });
  } else {
    // Verify parent MTF exists
    const parentMTF = availableMTFs.find(mtf => 
      mtf.facility_id === facility.parent_mtf_id || mtf.id === facility.parent_mtf_id
    );
    
    if (!parentMTF) {
      errors.push({
        field: 'parent_mtf_id',
        message: `Parent MTF '${facility.parent_mtf_id}' not found in available facilities`,
        icon: STATUS_ICONS.RED
      });
    }
  }
  
  // Validate distance
  if (facility.distance_to_parent_km < 0) {
    errors.push({
      field: 'distance_to_parent_km',
      message: 'Distance cannot be negative',
      icon: STATUS_ICONS.RED
    });
  }
  
  if (facility.distance_to_parent_km > 10) {
    warnings.push({
      field: 'distance_to_parent_km',
      message: `Distance ${facility.distance_to_parent_km} km is unusually far for staging facility`,
      icon: STATUS_ICONS.YELLOW
    });
  }
  
  // Validate facility type compatibility with echelon
  const echelonOption = PARENT_MTF_ASSIGNMENT_OPTIONS.find(
    opt => opt.value === facility.allocated_to_echelon
  );
  
  if (echelonOption && !echelonOption.typical_facilities.includes(facility.facility_type)) {
    warnings.push({
      field: 'facility_type',
      message: `${facility.facility_type} is not typical for ${facility.allocated_to_echelon} level`,
      icon: STATUS_ICONS.YELLOW
    });
  }
  
  return {
    valid: errors.length === 0,
    facility_id: facility.id,
    parent_mtf_id: facility.parent_mtf_id,
    errors,
    warnings,
    icon: errors.length > 0 ? STATUS_ICONS.RED : 
          warnings.length > 0 ? STATUS_ICONS.YELLOW : 
          STATUS_ICONS.GREEN
  };
}

/**
 * SF-004: Get parent MTF options for a specific staging facility type
 * Filters available MTFs based on what the staging facility type typically supports
 * @param {string} facilityType - CSU, PHA, HLZ, FST, FRS
 * @param {Array} availableMTFs - Array of available MTF objects
 * @returns {Array} Filtered MTF options for dropdown
 */
export function getParentMTFOptions(facilityType, availableMTFs = []) {
  // Define which MTF types each staging facility typically supports
  const supportedMTFTypes = {
    PHA: ['ROLE1', 'ROLE2_BASIC', 'ROLE2_ENHANCED'],  // PHA (forward holding) stages patients at R2B
    CSU: ['ROLE3', 'AIRFIELD'],                        // CSU (STRATEVAC staging) at APOD/R3 (AJMedP-1 para 3.18.6)
    HLZ: ['ROLE1', 'ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3'], // HLZ can support any
    FST: ['ROLE2_BASIC'],                            // FST attaches to Role 2B
    FRS: ['ROLE2_BASIC', 'ROLE2_ENHANCED']           // FRS attaches to Role 2B or 2E
  };
  
  const supported = supportedMTFTypes[facilityType] || [];
  
  return availableMTFs
    .filter(mtf => {
      const mtfType = mtf.type || mtf.facility_type || '';
      return supported.some(s => mtfType.toUpperCase().includes(s.replace('_', '')));
    })
    .map(mtf => ({
      value: mtf.facility_id || mtf.id,
      label: mtf.facility_name || mtf.name || mtf.facility_id,
      type: mtf.type || mtf.facility_type,
      position_feba_km: mtf.position_feba_km,
      hierarchy: mtf.hierarchy
    }));
}

/**
 * Get CSU standards by size
 * @param {string} size - SMALL, MEDIUM, or LARGE
 * @returns {Object} CSU standards
 */
export function getCSUStandards(size) {
  return CSU_NATO_STANDARDS[size] || CSU_NATO_STANDARDS.MEDIUM;
}

/**
 * Get PHA standards by size
 * @param {string} size - SMALL, MEDIUM, or LARGE
 * @returns {Object} PHA standards
 */
export function getPHAStandards(size) {
  return PHA_NATO_STANDARDS[size] || PHA_NATO_STANDARDS.MEDIUM;
}

/**
 * Get HLZ standards by size
 * @param {string} size - SMALL, MEDIUM, or LARGE
 * @returns {Object} HLZ standards
 */
export function getHLZStandards(size) {
  return HLZ_NATO_STANDARDS[size] || HLZ_NATO_STANDARDS.MEDIUM;
}

/**
 * CC-005: Get FST standards by variant
 * @param {string} variant - STANDARD or LIGHT
 * @returns {Object} FST standards
 */
export function getFSTStandards(variant = 'STANDARD') {
  return FST_NATO_STANDARDS[variant] || FST_NATO_STANDARDS.STANDARD;
}

/**
 * CC-005: Get FRS standards by variant
 * @param {string} variant - STANDARD
 * @returns {Object} FRS standards
 */
export function getFRSStandards(variant = 'STANDARD') {
  return FRS_NATO_STANDARDS[variant] || FRS_NATO_STANDARDS.STANDARD;
}

/**
 * Get facility standards by type and size
 * @param {string} facilityType - CSU, PHA, HLZ, FST, or FRS
 * @param {string} facilitySize - SMALL, MEDIUM, LARGE (or STANDARD, LIGHT for FST)
 * @returns {Object} Facility standards
 */
export function getFacilityStandards(facilityType, facilitySize) {
  switch (facilityType) {
    case 'PHA':
      // PHA = Patient Holding Area at Role 2B (forward holding). Uses old CSU physical standards.
      return getCSUStandards(facilitySize);
    case 'CSU':
      // CSU = Casualty Staging Unit at APOD (STRATEVAC staging). Uses old PHA physical standards.
      return getPHAStandards(facilitySize);
    case 'HLZ':
      return getHLZStandards(facilitySize);
    case 'FST':
      return getFSTStandards(facilitySize);
    case 'FRS':
      return getFRSStandards(facilitySize);
    default:
      return null;
  }
}

/**
 * Calculate facility requirements for given casualty load
 * @param {string} facilityType - CSU, PHA, or HLZ
 * @param {number} dailyCasualties - Expected daily casualties
 * @returns {Object} Recommended facility configuration
 */
export function calculateFacilityRequirements(facilityType, dailyCasualties) {
  let recommendedSize = 'SMALL';
  let quantity = 1;
  
  if (facilityType === 'PHA') {
    // PHA (Patient Holding Area, Role 2B) — forward holding, sized for casualty throughput
    if (dailyCasualties > 600) {
      recommendedSize = 'LARGE';
      quantity = Math.ceil(dailyCasualties / 800);
    } else if (dailyCasualties > 200) {
      recommendedSize = 'MEDIUM';
      quantity = Math.ceil(dailyCasualties / 500);
    } else {
      recommendedSize = 'SMALL';
      quantity = Math.ceil(dailyCasualties / 250);
    }
  } else if (facilityType === 'CSU') {
    // CSU (Casualty Staging Unit, APOD) — STRATEVAC staging, sized for surgical output
    const surgicalLoad = dailyCasualties * 0.25;
    if (surgicalLoad > 50) {
      recommendedSize = 'LARGE';
      quantity = Math.ceil(surgicalLoad / 60);
    } else if (surgicalLoad > 20) {
      recommendedSize = 'MEDIUM';
      quantity = Math.ceil(surgicalLoad / 35);
    } else {
      recommendedSize = 'SMALL';
      quantity = Math.ceil(surgicalLoad / 15);
    }
  } else if (facilityType === 'HLZ') {
    // HLZ sized for MEDEVAC missions, typically 30-40% of casualties by air
    const airEvacPatients = dailyCasualties * 0.35;
    const missionsNeeded = airEvacPatients / 4; // 4 patients per mission
    
    if (missionsNeeded > 80) {
      recommendedSize = 'LARGE';
      quantity = Math.ceil(missionsNeeded / 150);
    } else if (missionsNeeded > 30) {
      recommendedSize = 'MEDIUM';
      quantity = Math.ceil(missionsNeeded / 60);
    } else {
      recommendedSize = 'SMALL';
      quantity = Math.ceil(missionsNeeded / 15);
    }
  }
  
  const standards = getFacilityStandards(facilityType, recommendedSize);
  
  return {
    facility_type: facilityType,
    recommended_size: recommendedSize,
    quantity: quantity,
    standards: standards,
    daily_casualties: dailyCasualties,
    total_capacity: standards ? (standards.capacity_patients || standards.throughput?.patients_per_hour * 12) * quantity : 0,
    total_personnel: standards ? standards.personnel.total * quantity : 0
  };
}

/**
 * Calculate total personnel for facilities
 * @param {Array} facilities - Array of facility configurations
 * @returns {number} Total personnel
 */
export function calculateTotalPersonnel(facilities) {
  return facilities.reduce((total, facility) => {
    const standards = getFacilityStandards(facility.facility_type, facility.facility_size);
    return total + (standards ? standards.personnel.total : 0);
  }, 0);
}

/**
 * Calculate total patient capacity
 * @param {Array} facilities - Array of facility configurations
 * @returns {number} Total capacity
 */
export function calculateTotalCapacity(facilities) {
  return facilities.reduce((total, facility) => {
    const standards = getFacilityStandards(facility.facility_type, facility.facility_size);
    if (!standards) return total;
    
    const capacity = facility.facility_type === 'CSU' || facility.facility_type === 'PHA'
      ? standards.capacity_patients || standards.beds_total
      : 0;  // HLZ doesn't have static patient capacity
    
    return total + capacity;
  }, 0);
}

/**
 * Calculate total daily supply consumption
 * @param {Array} facilities - Array of facility configurations
 * @returns {Object} Total daily supplies
 */
export function calculateTotalDailySupplies(facilities) {
  const totals = {
    iv_fluids_liters: 0,
    dressings_units: 0,
    medications_doses: 0,
    food_rations: 0,
    blood_units: 0,
    oxygen_cylinders: 0
  };
  
  facilities.forEach(facility => {
    const standards = getFacilityStandards(facility.facility_type, facility.facility_size);
    if (standards && standards.daily_supply_consumption) {
      Object.keys(totals).forEach(key => {
        totals[key] += standards.daily_supply_consumption[key] || 0;
      });
    }
  });
  
  return totals;
}

/**
 * Get recommended facility size for echelon
 * @param {string} echelon - BATTALION, BRIGADE, DIVISION, CORPS
 * @returns {string} Recommended size
 */
export function getRecommendedFacilitySize(echelon) {
  const mapping = {
    'BATTALION': 'SMALL',
    'BRIGADE': 'SMALL',
    'DIVISION': 'MEDIUM',
    'CORPS': 'LARGE'
  };
  return mapping[echelon] || 'MEDIUM';
}

/**
 * Validate facility configuration
 * @param {Object} facility - Facility configuration
 * @returns {Object} Validation result
 */
export function validateFacility(facility) {
  const errors = [];
  const warnings = [];
  
  if (!facility.facility_type) {
    errors.push('Facility type is required');
  }
  
  if (!facility.facility_size) {
    errors.push('Facility size is required');
  }
  
  const standards = getFacilityStandards(facility.facility_type, facility.facility_size);
  if (!standards) {
    errors.push('Invalid facility type or size');
  }
  
  // Check if facility size is appropriate for echelon
  if (facility.allocated_to_echelon) {
    const recommended = getRecommendedFacilitySize(facility.allocated_to_echelon);
    if (facility.facility_size !== recommended) {
      warnings.push(
        `${facility.facility_size} facility size is not typical for ${facility.allocated_to_echelon} level. ` +
        `Recommended: ${recommended}`
      );
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? STATUS_ICONS.RED : 
          warnings.length > 0 ? STATUS_ICONS.YELLOW : 
          STATUS_ICONS.GREEN
  };
}

/**
 * Get facility type display name
 * @param {string} facilityType - CSU, PHA, HLZ, FST, or FRS
 * @returns {string} Display name
 */
export function getFacilityTypeName(facilityType) {
  const names = {
    CSU: 'Casualty Staging Unit',
    PHA: 'Patient Holding Area',
    HLZ: 'Helicopter Landing Zone',
    FST: 'Forward Surgical Team',
    FRS: 'Forward Resuscitation System'
  };
  return names[facilityType] || facilityType;
}

/**
 * Get facility icon by type
 * @param {string} facilityType - CSU, PHA, HLZ, FST, or FRS
 * @returns {string} Icon
 */
export function getFacilityIcon(facilityType) {
  const icons = {
    CSU: FACILITY_ICONS.CSU,
    PHA: FACILITY_ICONS.PHA,
    HLZ: FACILITY_ICONS.HLZ,
    FST: MEDICAL_ICONS.SURGERY || '🔪',
    FRS: MEDICAL_ICONS.SURGERY || '🔪'
  };
  return icons[facilityType] || FACILITY_ICONS.ROLE2;
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const STAGING_FACILITY_EDUCATIONAL = {
  key_principles: [
    'CSU: Pre-hospital staging for triage and stabilization',
    'PHA: Post-surgical holding before evacuation',
    'HLZ: Air evacuation infrastructure at all echelons',
    'FST: Forward surgical capability to meet Golden Hour',
    'FRS: Forward surgery with ICU capability',
    'Size scales with echelon and expected casualty load',
    'SF-004: Link each staging facility to its parent MTF for proper evacuation chain'
  ],
  
  ukraine_lessons: [
    'Underground facilities dramatically improved survival',
    'Multiple smaller facilities better than one large',
    'Rapid displacement capability essential',
    'Integration with civilian infrastructure helpful',
    'Night-only helicopter operations in contested areas',
    'FST attachment when evac times exceeded 60 minutes significantly improved survival'
  ],
  
  planning_factors: [
    'CSU: 1 per 200-300 daily casualties (brigade)',
    'PHA: Sized for 25% of casualties requiring surgery',
    'HLZ: Plan for 35% of casualties by air evacuation',
    'Setup times: CSU 2-8h, PHA 3-10h, HLZ 0.5-12h',
    'FST: Attach to R2B when POI→Surgery time > 60 min',
    'FST: 10-16 surgeries/day, 3-day consumables'
  ],
  
  common_errors: [
    'Single large facility vs distributed smaller ones',
    'Not planning for displacement',
    'Insufficient HLZ capacity for surge',
    'Ignoring supply consumption rates',
    'Not attaching FST when Golden Hour cannot be met',
    'Forgetting to plan FST evacuation for post-op patients',
    'SF-004: Not linking staging facility to parent MTF - breaks evacuation chain calculations'
  ],
  
  // CC-005: FST-specific guidance
  fst_attachment_guidance: {
    when_to_attach: [
      'Expected POI→Surgery time exceeds 60 minutes',
      'Multiple T1 casualties expected (offensive ops)',
      'Helicopter evacuation unreliable (contested airspace)',
      'Long ground evacuation routes (>25 km to Role 2E)'
    ],
    when_not_needed: [
      'POI→Surgery time reliably under 60 minutes',
      'Reliable helicopter evacuation available',
      'Role 2E/Role 3 within 20 km',
      'Low-intensity stability operations'
    ],
    considerations: [
      'FST cannot hold patients >24-48h post-op',
      'Requires blood product resupply every 2-3 days',
      'Surgeons need rotation after 72h continuous ops',
      'Setup requires 4 hours and secure location'
    ]
  },
  
  // SF-004: Parent MTF linkage guidance
  parent_mtf_linkage_guidance: {
    purpose: 'Links staging facilities to the MTF they support for evacuation chain calculations',
    typical_distances: {
      CSU_to_Role1: '0.5-2 km',
      HLZ_to_Role2B: '0.5-1 km',
      PHA_to_Role2E: '0.2-0.5 km',
      FST_to_Role2B: '0 km (co-located)'
    },
    why_it_matters: [
      'Calculator uses parent_mtf_id to determine evacuation flow',
      'Distance affects evacuation time calculations',
      'Unlinked facilities may be excluded from capacity calculations',
      'Reports require parent MTF for proper chain visualization'
    ]
  }
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const stagingFacilityStandardsConstants = {
  CSU_NATO_STANDARDS,
  PHA_NATO_STANDARDS,
  HLZ_NATO_STANDARDS,
  FST_NATO_STANDARDS,
  FRS_NATO_STANDARDS,
  FACILITY_SIZE_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  // SF-004: Parent MTF Linkage
  PARENT_MTF_ASSIGNMENT_OPTIONS,
  STAGING_FACILITY_SCHEMA,
  createStagingFacility,
  validateStagingFacilityParentLink,
  getParentMTFOptions,
  // Existing helpers
  getCSUStandards,
  getPHAStandards,
  getHLZStandards,
  getFSTStandards,
  getFRSStandards,
  getFacilityStandards,
  calculateFacilityRequirements,
  calculateTotalPersonnel,
  calculateTotalCapacity,
  calculateTotalDailySupplies,
  getRecommendedFacilitySize,
  validateFacility,
  getFacilityTypeName,
  getFacilityIcon,
  STAGING_FACILITY_EDUCATIONAL
};

export default stagingFacilityStandardsConstants;
